import { generateClient } from 'aws-amplify/api';

const client = generateClient();

// Message types for appointment notifications
export const MESSAGE_TYPES = {
  CONFIRMATION: 'confirmation',
  REMINDER_24HR: 'reminder_24hr',
  REMINDER_1HR: 'reminder_1hr'
};

export const MESSAGE_TYPE_LABELS = {
  [MESSAGE_TYPES.CONFIRMATION]: 'Confirmation (Immediate)',
  [MESSAGE_TYPES.REMINDER_24HR]: 'Reminder (24 Hours Before)',
  [MESSAGE_TYPES.REMINDER_1HR]: 'Reminder (1 Hour Before)'
};

// MRT (Motivational Readiness Theory) values
export const MRT_VALUES = {
  CONTROL: 'CONTROL',
  TSE: 'TSE',
  OE: 'OE',
  RNP: 'RNP',
  MSE: 'MSE',
  RSE: 'RSE'
};

export const MRT_LABELS = {
  [MRT_VALUES.CONTROL]: 'Control',
  [MRT_VALUES.TSE]: 'TSE (Task Self Efficacy)',
  [MRT_VALUES.OE]: 'OE (Outcomes Expectancy)',
  [MRT_VALUES.RNP]: 'RNP (Risk Need Perception)',
  [MRT_VALUES.MSE]: 'MSE (Maintenance Self Efficacy)',
  [MRT_VALUES.RSE]: 'RSE (Recovery Self Efficacy)'
};

// GraphQL queries and mutations (will be auto-generated after amplify push)
const listAppointmentMessageVariantTemplates = /* GraphQL */ `
  query ListAppointmentMessageVariantTemplates(
    $filter: ModelAppointmentMessageVariantTemplateFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listAppointmentMessageVariantTemplates(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        variantTrack
        messageType
        content
        isActive
        mrt
        response1
        response2
        createdAt
        updatedAt
        createdBy
        updatedBy
      }
      nextToken
    }
  }
`;

const createAppointmentMessageVariantTemplate = /* GraphQL */ `
  mutation CreateAppointmentMessageVariantTemplate(
    $input: CreateAppointmentMessageVariantTemplateInput!
  ) {
    createAppointmentMessageVariantTemplate(input: $input) {
      id
      variantTrack
      messageType
      content
      isActive
      mrt
      response1
      response2
      createdAt
      updatedAt
      createdBy
      updatedBy
    }
  }
`;

const updateAppointmentMessageVariantTemplate = /* GraphQL */ `
  mutation UpdateAppointmentMessageVariantTemplate(
    $input: UpdateAppointmentMessageVariantTemplateInput!
  ) {
    updateAppointmentMessageVariantTemplate(input: $input) {
      id
      variantTrack
      messageType
      content
      isActive
      mrt
      response1
      response2
      createdAt
      updatedAt
      createdBy
      updatedBy
    }
  }
`;

const deleteAppointmentMessageVariantTemplate = /* GraphQL */ `
  mutation DeleteAppointmentMessageVariantTemplate(
    $input: DeleteAppointmentMessageVariantTemplateInput!
  ) {
    deleteAppointmentMessageVariantTemplate(input: $input) {
      id
    }
  }
`;

/**
 * Fetch all appointment message variant templates
 * @returns {Promise<Array>} Array of all templates
 */
export const fetchAllVariantTemplates = async () => {
  try {
    let allItems = [];
    let nextToken = null;

    do {
      const result = await client.graphql({
        query: listAppointmentMessageVariantTemplates,
        variables: {
          limit: 100,
          nextToken
        }
      });

      const data = result.data.listAppointmentMessageVariantTemplates;
      allItems = [...allItems, ...data.items];
      nextToken = data.nextToken;
    } while (nextToken);

    return allItems;
  } catch (error) {
    console.error('[ERROR] Error fetching variant templates:', error);
    throw error;
  }
};

/**
 * Fetch templates organized by variant track
 * @returns {Promise<Object>} Templates organized by track number
 */
export const fetchTemplatesByTrack = async () => {
  try {
    const allTemplates = await fetchAllVariantTemplates();
    
    // Organize by track (1-4)
    const templatesByTrack = {
      1: {},
      2: {},
      3: {},
      4: {}
    };

    allTemplates.forEach(template => {
      const track = template.variantTrack;
      if (track >= 1 && track <= 4) {
        templatesByTrack[track][template.messageType] = template;
      }
    });

    return templatesByTrack;
  } catch (error) {
    console.error('[ERROR] Error fetching templates by track:', error);
    throw error;
  }
};

/**
 * Create a new variant template
 * @param {Object} params - Template parameters
 * @returns {Promise<Object>} Created template
 */
export const createVariantTemplate = async ({ variantTrack, messageType, content, isActive = true, mrt = null, response1 = null, response2 = null, createdBy }) => {
  try {
    const result = await client.graphql({
      query: createAppointmentMessageVariantTemplate,
      variables: {
        input: {
          variantTrack,
          messageType,
          content,
          isActive,
          mrt,
          response1,
          response2,
          createdBy,
          updatedBy: createdBy
        }
      }
    });

    return result.data.createAppointmentMessageVariantTemplate;
  } catch (error) {
    console.error('[ERROR] Error creating variant template:', error);
    throw error;
  }
};

/**
 * Update an existing variant template
 * @param {Object} params - Template parameters
 * @returns {Promise<Object>} Updated template
 */
export const updateVariantTemplate = async ({ id, content, isActive, mrt, response1, response2, updatedBy }) => {
  try {
    const result = await client.graphql({
      query: updateAppointmentMessageVariantTemplate,
      variables: {
        input: {
          id,
          content,
          isActive,
          mrt,
          response1,
          response2,
          updatedBy
        }
      }
    });

    return result.data.updateAppointmentMessageVariantTemplate;
  } catch (error) {
    console.error('[ERROR] Error updating variant template:', error);
    throw error;
  }
};

/**
 * Delete a variant template
 * @param {string} id - Template ID
 * @returns {Promise<Object>} Deleted template
 */
export const deleteVariantTemplate = async (id) => {
  try {
    const result = await client.graphql({
      query: deleteAppointmentMessageVariantTemplate,
      variables: {
        input: { id }
      }
    });

    return result.data.deleteAppointmentMessageVariantTemplate;
  } catch (error) {
    console.error('[ERROR] Error deleting variant template:', error);
    throw error;
  }
};

/**
 * Save or update a full track (all 3 message types)
 * @param {number} trackNumber - Track number (1-4)
 * @param {Object} messages - Object with confirmation, reminder_24hr, reminder_1hr
 * @param {string} updatedBy - User making the change
 * @param {Object} existingTemplates - Existing templates for this track
 * @returns {Promise<Object>} Saved templates
 */
export const saveTrack = async (trackNumber, messages, updatedBy, existingTemplates = {}, mrt = null, responses = {}) => {
  try {
    const savedTemplates = {};

    for (const messageType of Object.values(MESSAGE_TYPES)) {
      const content = messages[messageType] || '';
      const existing = existingTemplates[messageType];
      const response1 = responses[messageType]?.response1 || null;
      const response2 = responses[messageType]?.response2 || null;

      if (existing) {
        // Update existing template
        savedTemplates[messageType] = await updateVariantTemplate({
          id: existing.id,
          content,
          isActive: true,
          mrt,
          response1,
          response2,
          updatedBy
        });
      } else if (content.trim()) {
        // Create new template only if content is not empty
        savedTemplates[messageType] = await createVariantTemplate({
          variantTrack: trackNumber,
          messageType,
          content,
          isActive: true,
          mrt,
          response1,
          response2,
          createdBy: updatedBy
        });
      }
    }

    return savedTemplates;
  } catch (error) {
    console.error('[ERROR] Error saving track:', error);
    throw error;
  }
};

/**
 * Get template for a specific track and message type
 * @param {number} variantTrack - Track number (1-4)
 * @param {string} messageType - Message type
 * @returns {Promise<Object|null>} Template or null
 */
export const getTemplateByTrackAndType = async (variantTrack, messageType) => {
  try {
    const templates = await fetchAllVariantTemplates();
    return templates.find(
      t => t.variantTrack === variantTrack && t.messageType === messageType && t.isActive
    ) || null;
  } catch (error) {
    console.error('[ERROR] Error getting template:', error);
    throw error;
  }
};
